import axios from 'axios';

export interface WebsitePage {
  id: number;
  title: string;
  slug: string;
  content: string;
  url: string;
  order: number;
}

const wpClient = axios.create({
  baseURL: '/wp-proxy',
});

const EXCLUDED_SLUGS = ['danke', 'hallo-welt'];

export const WebsiteService = {
  async fetchAllPages(): Promise<WebsitePage[]> {
    const response = await wpClient.get('/wp-json/wp/v2/pages', {
      params: { per_page: 50, _fields: 'id,title,slug,content,link,menu_order' },
    });

    const pages: WebsitePage[] = response.data
      .filter((p: any) => !EXCLUDED_SLUGS.includes(p.slug))
      .map((p: any) => ({
        id: p.id,
        title: p.title.rendered,
        slug: p.slug,
        content: sanitizeContent(p.content.rendered),
        url: p.link,
        order: p.menu_order || 0,
      }));

    pages.sort((a, b) => {
      if (a.slug === '' || a.slug === 'home') return -1;
      if (b.slug === '' || b.slug === 'home') return 1;
      return a.order - b.order;
    });

    return pages;
  },
};

function sanitizeContent(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('form, script, iframe, noscript, style').forEach((el) => el.remove());
  doc.querySelectorAll('input, button, textarea, select').forEach((el) => el.remove());
  doc.querySelectorAll('[class*="cookie"], [class*="popup"], [class*="modal"]').forEach((el) =>
    el.remove(),
  );

  // Remove interactive elements: "Mehr erfahren", arrows, tab controls, step indicators
  doc.querySelectorAll('[role="tablist"], [class*="swiper-button"], [class*="arrow"]').forEach(
    (el) => el.remove(),
  );

  // Remove marquee/ticker decorative text bands
  doc.querySelectorAll('[class*="marquee"], [class*="ticker"]').forEach((el) => el.remove());

  // Remove navigation hints like "Wischen oder klicken"
  doc.querySelectorAll('p, span, div').forEach((el) => {
    const text = el.textContent?.trim().toLowerCase() || '';
    if (text === 'wischen oder klicken' || text.includes('wischen oder klicken')) {
      el.remove();
    }
  });

  // Expand all collapsed/accordion content — make hidden panels visible
  doc.querySelectorAll('details').forEach((el) => el.setAttribute('open', ''));
  doc.querySelectorAll('[hidden], [aria-hidden="true"]').forEach((el) => {
    el.removeAttribute('hidden');
    el.removeAttribute('aria-hidden');
  });
  // Strip all inline styles that could hide content
  doc.querySelectorAll('[style]').forEach((el) => {
    el.removeAttribute('style');
  });

  // Remove CTA links/buttons
  const ctaTexts = [
    'mehr erfahren',
    'app herunterladen',
    'jetzt testen',
    'jetzt kostenlos starten',
    'unternehmen registrieren',
    'zur nutzer-app',
  ];
  doc.querySelectorAll('a').forEach((a) => {
    const text = a.textContent?.trim().toLowerCase() || '';
    if (ctaTexts.includes(text)) {
      a.remove();
    } else {
      const span = doc.createElement('span');
      span.innerHTML = a.innerHTML;
      a.replaceWith(span);
    }
  });

  // Remove standalone CTA-like elements (with or without child elements)
  doc.querySelectorAll('span, div, p, summary, dt').forEach((el) => {
    const text = el.textContent?.trim().toLowerCase() || '';
    if (ctaTexts.includes(text)) {
      el.remove();
    }
  });

  // Remove bare text nodes that match CTA phrases
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const toRemove: Node[] = [];
  while (walker.nextNode()) {
    const text = walker.currentNode.textContent?.trim().toLowerCase() || '';
    if (ctaTexts.includes(text)) {
      toRemove.push(walker.currentNode);
    }
  }
  toRemove.forEach((node) => node.parentNode?.removeChild(node));

  return doc.body.innerHTML;
}
